import { api } from 'dicomweb-client';
import { calculateSUVScalingFactors } from '@cornerstonejs/calculate-suv';
import { getPTImageIdInstanceMetadata } from './getPTImageIdInstanceMetadata';
import { utilities, metaData } from '@cornerstonejs/core';
import cornerstoneDICOMImageLoader from '@cornerstonejs/dicom-image-loader';
import {
  utilities as metadataUtilities,
  Enums as metadataEnums,
} from '@cornerstonejs/metadata';

import ptScalingMetaDataProvider from './ptScalingMetaDataProvider';
import { convertMultiframeImageIds } from './convertMultiframeImageIds';

const { calibratedPixelSpacingMetadataProvider, getPixelSpacingInformation } =
  utilities;

const { addDicomWebInstance, Tags } = metadataUtilities;
const { MetadataModules } = metadataEnums;

const SOP_INSTANCE_UID = Tags.resolveHexFromKeyword('SOPInstanceUID');
const SERIES_INSTANCE_UID = Tags.resolveHexFromKeyword('SeriesInstanceUID');
const MODALITY = Tags.resolveHexFromKeyword('Modality');

/**
 * Uses dicomweb-client to fetch metadata of a study, cache it in cornerstone,
 * and return a list of imageIds for the frames.
 *
 * Uses the app config to choose which study to fetch, and which
 * dicom-web server to fetch it from.
 *
 * @param {object} options
 * @param {boolean} [options.useLegacyWadoRs=false] - When true, store instances only in the legacy wadors metaDataManager; when false, use the typed metadata framework (@cornerstonejs/metadata)
 * @returns {string[]} An array of imageIds for instances in the study.
 */

export default async function createImageIdsAndCacheMetaData({
  StudyInstanceUID,
  SeriesInstanceUID,
  SOPInstanceUID = null,
  wadoRsRoot,
  client = null,
  convertMultiframe = true,
  useLegacyWadoRs = false,
}) {
  const studySearchOptions = {
    studyInstanceUID: StudyInstanceUID,
    seriesInstanceUID: SeriesInstanceUID,
  };

  client = client || new api.DICOMwebClient({ url: wadoRsRoot });
  let instances = await client.retrieveSeriesMetadata(studySearchOptions);

  // if sop instance is provided we should filter the instances to only include the one we want
  if (SOPInstanceUID) {
    instances = instances.filter((instance) => {
      return instance[SOP_INSTANCE_UID].Value[0] === SOPInstanceUID;
    });
  }

  const modality = instances[0][MODALITY].Value[0];
  let imageIds = instances.map((instanceMetaData) => {
    const SeriesInstanceUID = instanceMetaData[SERIES_INSTANCE_UID].Value[0];
    const SOPInstanceUIDToUse =
      SOPInstanceUID || instanceMetaData[SOP_INSTANCE_UID].Value[0];

    const prefix = 'wadors:';

    const imageId =
      prefix +
      wadoRsRoot +
      '/studies/' +
      StudyInstanceUID.trim() +
      '/series/' +
      SeriesInstanceUID.trim() +
      '/instances/' +
      SOPInstanceUIDToUse.trim() +
      '/frames/1';

    cornerstoneDICOMImageLoader.wadors.metaDataManager.add(
      imageId,
      instanceMetaData
    );

    // NATURALIZED is keyed by base image id; skip when already cached so a
    // second select of the same series does not spam "Metadata add skipped".
    if (
      !useLegacyWadoRs &&
      metaData.get(MetadataModules.NATURALIZED, imageId) === undefined
    ) {
      addDicomWebInstance(imageId, instanceMetaData);
    }

    return imageId;
  });

  // if the image ids represent multiframe information, creates a new list with one image id per frame
  // if not multiframe data available, just returns the same list given
  if (convertMultiframe) {
    const originalImageIds = [...imageIds];
    imageIds = convertMultiframeImageIds(imageIds);
  }

  if (!useLegacyWadoRs) {
    imageIds.forEach((imageId) => {
      const instance = metaData.get('instanceOrig', imageId);

      if (!instance) {
        return;
      }

      // Add calibrated pixel spacing from the naturalized instance
      const pixelSpacingInformation = getPixelSpacingInformation(instance);
      const pixelSpacing = pixelSpacingInformation?.PixelSpacing;

      if (pixelSpacing) {
        calibratedPixelSpacingMetadataProvider.add(imageId, {
          rowPixelSpacing: parseFloat(pixelSpacing[0]),
          columnPixelSpacing: parseFloat(pixelSpacing[1]),
          type: pixelSpacingInformation.type,
        });
      }
    });
  }

  // we don't want to add non-pet
  // Note: for 99% of scanners SUV calculation is consistent bw slices
  if (modality === 'PT') {
    const InstanceMetadataArray = [];
    imageIds.forEach((imageId) => {
      const instanceMetadata = getPTImageIdInstanceMetadata(imageId);

      // TODO: Temporary fix because static-wado is producing a string, not an array of values
      // (or maybe dcmjs isn't parsing it correctly?)
      // It's showing up like 'DECY\\ATTN\\SCAT\\DTIM\\RAN\\RADL\\DCAL\\SLSENS\\NORM'
      // but calculate-suv expects ['DECY', 'ATTN', ...]
      if (typeof instanceMetadata.CorrectedImage === 'string') {
        instanceMetadata.CorrectedImage =
          instanceMetadata.CorrectedImage.split('\\');
      }

      if (instanceMetadata) {
        InstanceMetadataArray.push(instanceMetadata);
      }
    });
    if (InstanceMetadataArray.length) {
      try {
        const suvScalingFactors = calculateSUVScalingFactors(
          InstanceMetadataArray
        );
        InstanceMetadataArray.forEach((instanceMetadata, index) => {
          ptScalingMetaDataProvider.addInstance(
            imageIds[index],
            suvScalingFactors[index]
          );
        });
      } catch (error) {
        console.log(error);
      }
    }
  }

  return imageIds;
}
